import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { accountPhoneNumbersRoute, phoneNumbersRoute } from 'src/app/app-routing.module';
import { RegisterOptions } from 'src/app/common-components/register/models/register-options';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { ExtensionSaveModel } from 'src/app/pages/extension-details/models/extension-detail-save.model';
import {
   PhoneNumberDetailDeleteEvent,
   PhoneNumberDetailReleaseEvent,
} from 'src/app/pages/phone-number-details/models/phone-number-detail-form-submit-event';
import { PhoneNumberPrivileges } from 'src/app/pages/phone-number-details/models/phone-number-privileges';
import { PhoneNumberOrderingOptions, PhoneNumberRegisterOptions } from '../../models/phone-number-register-options';
import { PhoneNumberRegisterListItem } from '../../models/phone-number-register-results.model';
import { selectQuickFilters } from '../../state/phone-number-register.reducer';
import { FormatPhoneNumbersPipe } from 'src/app/pipes/format-phone-numbers/format-phone-numbers.pipe';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-system-phone-number-register-view',
    styleUrls: ['./system-phone-number-register-view.component.scss'],
    templateUrl: './system-phone-number-register-view.component.html',
    standalone: false
})
export class SystemPhoneNumberRegisterViewComponent extends EntityRegisterView<
   PhoneNumberRegisterListItem,
   PhoneNumberRegisterOptions,
   PhoneNumberOrderingOptions
> {
   protected selectQuickFilters(): (state: object) => RegisterOptions | null {
      return selectQuickFilters;
   }
   @Input() includeInactive: boolean;

   @Input() phoneNumberPrivileges: PhoneNumberPrivileges;

   @Output() numberReleased = new EventEmitter<PhoneNumberDetailReleaseEvent>();

   @Output() numberDeleted = new EventEmitter<PhoneNumberDetailDeleteEvent>();

   constructor(public store: Store, private clipboard: Clipboard, private snackbar: MatSnackBar, private formatPhoneNumbersPipe: FormatPhoneNumbersPipe) {
      super(store);
   }

   getDynamicRouterLink(phoneNumber: PhoneNumberRegisterListItem) {
      if (phoneNumber.accountName) {
         return `/${accountPhoneNumbersRoute}/${phoneNumber.phoneNumberId}`;
      }
      return phoneNumber.phoneNumberId !== null ? this.getRouterLink(phoneNumber.phoneNumberId) : '/phonennumbers';
   }

   getRouterLink(id: number) {
      return `/${phoneNumbersRoute}/${id}`;
   }

   canRelease(phoneNumber: PhoneNumberRegisterListItem): boolean {
      const hasAssociatedAccount = !!phoneNumber.accountName;
      const hasPrivilege = this.phoneNumberPrivileges.canAdd && this.phoneNumberPrivileges.canEdit;
      return hasPrivilege && hasAssociatedAccount;
   }

   onReleaseAccountClick(phoneNumber: PhoneNumberRegisterListItem): void {
      const releaseEventModel: PhoneNumberDetailReleaseEvent = {
         phoneNumber: {
            phoneNumberId: phoneNumber.phoneNumberId ?? null,
         },
         phonesUsingForCallerId: (phoneNumber.phonesUsingForCallerId as ExtensionSaveModel[]) ?? [],
         faxesUsingForCallerId: (phoneNumber.faxesUsingForCallerId as ExtensionSaveModel[]) ?? [],
      };

      this.numberReleased.emit(releaseEventModel);
   }

   canDelete(phoneNumber: PhoneNumberRegisterListItem): boolean {
      return this.phoneNumberPrivileges.canDelete;
   }

   copyPhoneNumberToClipboard(phoneNumber: string) {
      const formattedPhoneNumber = this.formatPhoneNumbersPipe.transform(phoneNumber);
      const copied: boolean = this.clipboard.copy(formattedPhoneNumber);

      if (copied) {
         this.snackbar.open('Copied to Clipboard');
      }
   }

   onDeleteClick(phoneNumber: PhoneNumberRegisterListItem): void {
      const deleteEventModel: PhoneNumberDetailDeleteEvent = {
         phoneNumber: {
            phoneNumberId: phoneNumber.phoneNumberId ?? null,
         },
         phonesUsingForCallerId: (phoneNumber?.phonesUsingForCallerId as ExtensionSaveModel[]) ?? [],
         faxesUsingForCallerId: (phoneNumber?.faxesUsingForCallerId as ExtensionSaveModel[]) ?? [],
      };

      this.numberDeleted.emit(deleteEventModel);
   }
}
